import { useEffect, useState } from 'react'
import { useAuth } from '../../auth/AuthProvider'
import { supabase } from '../../lib/supabase'

type ReleaseHistoryItem = {
  release_id: number
  version: string
  titulo: string
  status: string
  published_at: string | null
  published_by: string
  has_snapshot: boolean
  rollback_from_version: string | null
  is_current: boolean
}

function formatDate(value: string | null) {
  if (!value) return '—'
  const date = new Date(value)
  return Number.isNaN(date.getTime()) ? '—' : date.toLocaleString('pt-BR')
}

function errorMessage(error: unknown, fallback: string) {
  if (error instanceof Error) return error.message
  if (error && typeof error === 'object' && 'message' in error) {
    const message = String((error as { message?: unknown }).message ?? '').trim()
    if (message) return message
  }
  return fallback
}

export function ReleaseHistory() {
  const { profile } = useAuth()
  const [items, setItems] = useState<ReleaseHistoryItem[]>([])
  const [loading, setLoading] = useState(true)
  const [savingId, setSavingId] = useState<number | null>(null)
  const [expanded, setExpanded] = useState(false)
  const [error, setError] = useState('')
  const [message, setMessage] = useState('')

  async function load() {
    setLoading(true)
    setError('')
    try {
      const result = await supabase.rpc('list_release_history')
      if (result.error) throw result.error
      setItems((result.data ?? []) as ReleaseHistoryItem[])
    } catch (err) {
      setItems([])
      setError(errorMessage(err, 'Não foi possível carregar o histórico de versões.'))
    } finally {
      setLoading(false)
    }
  }

  useEffect(() => {
    void load()
  }, [])

  async function restore(item: ReleaseHistoryItem) {
    if (profile?.role !== 'administrador' || !item.has_snapshot || item.is_current) return

    const confirmation = window.prompt(
      `Restaurar o conteúdo integral da versão ${item.version}?\n\nO Suite criará uma NOVA versão publicada. O histórico atual não será apagado.\n\nDigite RESTAURAR ${item.version} para confirmar.`,
    )
    if (confirmation !== `RESTAURAR ${item.version}`) return

    setSavingId(item.release_id)
    setError('')
    setMessage('')
    try {
      const result = await supabase.rpc('rollback_release', {
        target_release_id: item.release_id,
      })
      if (result.error) throw result.error

      const payload = result.data as { version?: string; restored_from_version?: string } | null
      setMessage(
        `Restauração concluída. A versão ${payload?.version ?? 'nova'} foi publicada com o conteúdo da ${payload?.restored_from_version ?? item.version}.`,
      )
      await load()
      window.setTimeout(() => window.location.reload(), 900)
    } catch (err) {
      setError(errorMessage(err, 'Não foi possível restaurar a versão.'))
    } finally {
      setSavingId(null)
    }
  }

  const current = items.find((item) => item.is_current)
  const historical = items.filter((item) => !item.is_current)

  return (
    <section
      style={{
        margin: '18px 0',
        padding: 18,
        border: '1px solid var(--line)',
        borderRadius: 18,
        background: '#fff',
        boxShadow: '0 9px 28px rgba(16,34,49,.045)',
      }}
    >
      <div className="pending-head" style={{ marginBottom: expanded ? 14 : 0 }}>
        <div>
          <p className="eyebrow">VERSIONAMENTO E RECUPERAÇÃO</p>
          <h2 style={{ marginBottom: 4 }}>Histórico de versões</h2>
          <p className="muted" style={{ margin: 0 }}>
            {loading
              ? 'Carregando histórico...'
              : current
                ? `Atual: ${current.version} · ${historical.length} versão(ões) anterior(es)`
                : 'Nenhuma release publicada encontrada.'}
          </p>
        </div>
        <button className="secondary-button compact" disabled={loading} onClick={() => setExpanded((value) => !value)}>
          {expanded ? 'Ocultar histórico' : 'Ver histórico'}
        </button>
      </div>

      {message && <div className="notice success">{message}</div>}
      {error && <div className="notice error">{error}</div>}

      {expanded && !loading && (
        <div style={{ display: 'grid', gap: 9 }}>
          {items.map((item) => (
            <article
              key={item.release_id}
              style={{
                display: 'grid',
                gridTemplateColumns: 'minmax(0,1fr) auto',
                alignItems: 'center',
                gap: 14,
                padding: '12px 13px',
                border: item.is_current ? '1px solid rgba(22,132,91,.24)' : '1px solid #e8eef2',
                borderRadius: 13,
                background: item.is_current ? 'rgba(22,132,91,.035)' : '#fbfcfd',
              }}
            >
              <div style={{ minWidth: 0 }}>
                <div style={{ display: 'flex', alignItems: 'center', gap: 7, flexWrap: 'wrap' }}>
                  <strong>{item.version}</strong>
                  {item.is_current && <span className="status-chip">Atual</span>}
                  {item.has_snapshot ? (
                    <span className="status-chip" style={{ borderColor: 'rgba(0,174,202,.22)', background: 'rgba(0,174,202,.06)', color: '#007f9f' }}>Snapshot</span>
                  ) : (
                    <span className="status-chip warning">Histórico apenas</span>
                  )}
                  {item.rollback_from_version && (
                    <span className="status-chip warning">Restaurada de {item.rollback_from_version}</span>
                  )}
                </div>
                <span style={{ display: 'block', marginTop: 5, color: '#102231', fontSize: '.73rem' }}>{item.titulo}</span>
                <span style={{ display: 'block', marginTop: 4, color: '#647787', fontSize: '.66rem' }}>
                  {formatDate(item.published_at)} · publicado por {item.published_by}
                </span>
              </div>

              {profile?.role === 'administrador' && !item.is_current && item.has_snapshot && (
                <button
                  type="button"
                  className="danger-button compact"
                  disabled={savingId !== null}
                  onClick={() => void restore(item)}
                >
                  {savingId === item.release_id ? 'Restaurando...' : `Restaurar ${item.version}`}
                </button>
              )}
            </article>
          ))}
          {items.length === 0 && <div className="empty-state">Nenhuma versão encontrada.</div>}
        </div>
      )}

      <div className="notice info" style={{ marginBottom: 0 }}>
        O rollback nunca apaga o histórico: ele restaura as seis bases e publica uma nova versão. Releases anteriores ao primeiro snapshot permanecem disponíveis para consulta, mas não podem ser restauradas automaticamente.
      </div>
    </section>
  )
}
